// Blog index + post styles in the marketing-site design (Plus Jakarta Sans, brand blues, hairline cards).
// Kept as plain strings with literal values (no .mk variables) because the internal blog editor
// injects them into its "manage" grid and into its preview iframe, outside the marketing shell.
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/funnel-display/700.css';

export function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const SANS = "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";
const DISPLAY = `'Funnel Display', ${SANS}`;

export const blogStyles = `
  .tc-blog { font-family: ${SANS}; color: #0A1A3A; line-height: 1.5; -webkit-font-smoothing: antialiased; }
  .tc-blog a { text-decoration: none; }

  /* Hero (the internal editor's manage view; the public page uses the site hero) */
  .tc-blog-hero { padding: 72px 24px 56px; text-align: center; }
  .tc-blog-hero-center { max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; align-items: center; }
  .tc-blog-hero-label { font-size: 13px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #0066E6; margin: 0 0 14px; }
  .tc-blog-hero-title { font-family: ${DISPLAY}; font-size: clamp(40px, 5vw, 68px); font-weight: 700; line-height: 1.04; letter-spacing: -0.04em; color: #0A1A3A; margin: 0 0 22px; text-wrap: balance; }
  .tc-blog-hero-title span { color: #0066E6; }
  .tc-blog-hero-subtitle { font-size: clamp(17px, 1.5vw, 20px); line-height: 1.55; color: #3E4C6B; max-width: 680px; margin: 0; }

  /* Featured post */
  .tc-blog-featured-wrap { max-width: 1200px; margin: 0 auto; padding: 0 0 56px; }
  .tc-blog-featured {
    display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    background: #fff; border: 1px solid #E3EAF5; border-radius: 28px; overflow: hidden; color: #0A1A3A;
    transition: border-color .2s, box-shadow .2s, transform .2s;
  }
  .tc-blog-featured:hover { color: #0A1A3A; border-color: #C9D5E8; box-shadow: 0 18px 36px -24px rgba(0,62,133,0.4); transform: translateY(-2px); }
  .tc-blog-featured-img-wrap { overflow: hidden; background: #F5F8FE; }
  .tc-blog-featured-img { width: 100%; height: 100%; min-height: 360px; object-fit: cover; display: block; }
  .tc-blog-featured-body { padding: clamp(28px, 4vw, 48px); display: flex; flex-direction: column; justify-content: center; }
  .tc-blog-featured-category { font-size: 13px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #0066E6; margin: 0 0 14px; }
  .tc-blog-featured-title { font-size: clamp(24px, 2.4vw, 32px); font-weight: 700; line-height: 1.2; letter-spacing: -0.025em; margin: 0 0 14px; transition: color .2s; text-wrap: balance; }
  .tc-blog-featured:hover .tc-blog-featured-title { color: #0066E6; }
  .tc-blog-featured-excerpt { font-size: 16px; color: #3E4C6B; line-height: 1.6; margin: 0 0 24px; }
  .tc-blog-featured-footer { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; font-size: 14px; font-weight: 600; color: #5B6B8A; }
  .tc-blog-featured-footer span { display: inline-flex; align-items: center; gap: 6px; }
  .tc-blog-featured-readmore { margin-left: auto; font-weight: 700; color: #0066E6; }
  .tc-blog-featured-readmore svg { transition: transform .2s; }
  .tc-blog-featured:hover .tc-blog-featured-readmore svg { transform: translateX(3px); }

  /* "Latest" divider */
  .tc-blog-divider { max-width: 1200px; margin: 0 auto; padding: 0 0 28px; }
  .tc-blog-divider-label { display: inline-block; font-size: 13px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #0066E6; padding-bottom: 12px; border-bottom: 2px solid #0066E6; }
  .tc-blog-divider-line { border: none; border-top: 1px solid #E3EAF5; margin: -1px 0 0; }

  /* Grid + cards */
  .tc-blog-section { padding: 8px 0 24px; }
  .tc-blog-section-inner { max-width: 1200px; margin: 0 auto; }
  .tc-blog-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
  .tc-blog-card {
    display: flex; flex-direction: column; height: 100%;
    background: #fff; border: 1px solid #E3EAF5; border-radius: 24px; overflow: hidden; color: #0A1A3A;
    transition: border-color .2s, box-shadow .2s, transform .2s;
  }
  .tc-blog-card:hover { color: #0A1A3A; border-color: #C9D5E8; box-shadow: 0 18px 36px -24px rgba(0,62,133,0.4); transform: translateY(-2px); }
  .tc-blog-card-img-wrap { overflow: hidden; background: #F5F8FE; }
  .tc-blog-card-img { width: 100%; aspect-ratio: 16/10; object-fit: cover; display: block; }
  .tc-blog-card-body { padding: 22px 22px 24px; display: flex; flex-direction: column; flex: 1; }
  .tc-blog-card-category { font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #0066E6; margin: 0 0 10px; }
  .tc-blog-card-title { font-size: 18px; font-weight: 700; line-height: 1.3; letter-spacing: -0.015em; margin: 0 0 8px; transition: color .2s; }
  .tc-blog-card:hover .tc-blog-card-title { color: #0066E6; }
  .tc-blog-card-excerpt { flex: 1; font-size: 14px; line-height: 1.6; color: #3E4C6B; margin: 0 0 18px; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
  .tc-blog-card-footer { display: flex; align-items: center; justify-content: space-between; padding-top: 14px; border-top: 1px solid #EDF1F8; font-size: 13px; font-weight: 600; color: #5B6B8A; }
  .tc-blog-card-footer-left { display: flex; align-items: center; gap: 12px; }
  .tc-blog-card-footer-left span { display: inline-flex; align-items: center; gap: 5px; }
  .tc-blog-card-arrow { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: #E8F2FF; color: #0066E6; transition: background .2s, color .2s; }
  .tc-blog-card:hover .tc-blog-card-arrow { background: #0066E6; color: #fff; }

  .tc-blog-empty { padding: 24px 0 72px; text-align: center; font-size: 16px; color: #5B6B8A; }

  @media (max-width: 960px) {
    .tc-blog-featured { grid-template-columns: minmax(0, 1fr); }
    .tc-blog-featured-img { min-height: 240px; }
    .tc-blog-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 560px) {
    .tc-blog-grid { grid-template-columns: minmax(0, 1fr); }
    .tc-blog-featured-readmore { margin-left: 0; }
  }
`;

export const postStyles = `
  .tc-blogpost { font-family: ${SANS}; color: #0A1A3A; line-height: 1.5; -webkit-font-smoothing: antialiased; }
  .tc-blogpost-header { max-width: 1200px; margin: 0 auto; padding: 40px 0 0; }
  .tc-blogpost-back { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; margin-bottom: 20px; font-size: 14px; font-weight: 600; color: #5B6B8A; text-decoration: none; }
  .tc-blogpost-back:hover { color: #0066E6; }
  .tc-blogpost-hero-grid { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); gap: 56px; align-items: center; }
  .tc-blogpost-eyebrow { font-size: 13px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #0066E6; margin: 0 0 14px; }
  .tc-blogpost-title { font-family: ${DISPLAY}; font-size: clamp(34px, 4.2vw, 56px); font-weight: 700; line-height: 1.06; letter-spacing: -0.04em; color: #0A1A3A; margin: 0 0 20px; text-wrap: balance; }
  .tc-blogpost-meta { font-size: 15px; font-weight: 600; color: #5B6B8A; }
  .tc-blogpost-cover-img { width: 100%; aspect-ratio: 4/3; border-radius: 28px; border: 1px solid #E3EAF5; object-fit: cover; background: #F5F8FE; display: block; }

  .tc-blogpost-divider { max-width: 1200px; margin: 56px auto 0; }
  .tc-blogpost-divider hr { border: none; border-top: 1px solid #EDF1F8; margin: 0; }

  .tc-blogpost-main { max-width: 1200px; margin: 56px auto 112px; display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 64px; align-items: start; }
  .tc-blogpost-body { min-width: 0; font-size: 17px; line-height: 1.8; color: #3E4C6B; }
  .tc-blogpost-body h2 { font-size: clamp(24px, 2.2vw, 30px); font-weight: 700; line-height: 1.2; letter-spacing: -0.025em; color: #0A1A3A; margin: 48px 0 16px; }
  .tc-blogpost-body h3 { font-size: 20px; font-weight: 700; line-height: 1.3; letter-spacing: -0.015em; color: #0A1A3A; margin: 36px 0 12px; }
  .tc-blogpost-body p { margin: 0 0 22px; }
  .tc-blogpost-body ul, .tc-blogpost-body ol { margin: 0 0 22px; padding-left: 24px; }
  .tc-blogpost-body ul { list-style: disc; }
  .tc-blogpost-body ol { list-style: decimal; }
  .tc-blogpost-body li { margin-bottom: 8px; }
  .tc-blogpost-body strong { color: #0A1A3A; }
  .tc-blogpost-body img { max-width: 100%; height: auto; border-radius: 20px; }
  .tc-blogpost-body blockquote { margin: 32px 0; padding: 20px 24px; border-left: 3px solid #0066E6; border-radius: 0 16px 16px 0; background: #F5F8FE; color: #1E2D4F; font-size: 18px; font-weight: 500; }
  .tc-blogpost-body a { color: #0066E6; text-decoration: underline; text-underline-offset: 3px; }
  .tc-blogpost-body a:hover { color: #0050B0; }

  /* Sidebar */
  .tc-blogpost-sidebar { position: sticky; top: 104px; align-self: start; display: flex; flex-direction: column; gap: 24px; }
  .tc-blogpost-cta-card { padding: 28px; border-radius: 28px; border: 1px solid #E3EAF5; background: #fff; box-shadow: 0 40px 80px -40px rgba(0,62,133,0.35); }
  .tc-blogpost-cta-card h3 { font-size: 22px; font-weight: 700; line-height: 1.25; letter-spacing: -0.02em; color: #0A1A3A; margin: 0 0 10px; }
  .tc-blogpost-cta-card p { font-size: 15px; line-height: 1.6; color: #3E4C6B; margin: 0 0 20px; }
  .tc-blogpost-cta-form { display: flex; flex-direction: column; gap: 12px; }
  .tc-blogpost-cta-form input { width: 100%; min-height: 48px; padding: 12px 14px; border-radius: 12px; border: 1px solid #8291AD; background: #fff; color: #0A1A3A; font: 500 15px ${SANS}; }
  .tc-blogpost-cta-form input::placeholder { color: #5B6B8A; }
  .tc-blogpost-cta-form input:focus { outline: none; border-color: #007BFF; box-shadow: 0 0 0 3px rgba(0,123,255,0.18); }
  .tc-blogpost-cta-form button { display: inline-flex; align-items: center; justify-content: center; min-height: 48px; padding: 0 24px; border: 1px solid transparent; border-radius: 999px; background: #0066E6; color: #fff; font: 600 16px ${SANS}; cursor: pointer; box-shadow: 0 14px 28px -14px rgba(0,102,230,0.8); transition: transform .15s, background .15s; }
  .tc-blogpost-cta-form button:hover { background: #0050B0; transform: translateY(-1px); }
  .tc-blogpost-connect { margin-top: 22px; padding-top: 20px; border-top: 1px solid #E3EAF5; }
  .tc-blogpost-connect-icons { display: flex; gap: 10px; justify-content: center; }
  .tc-blogpost-connect-icons a { width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; border-radius: 12px; background: #E8F2FF; color: #0066E6; transition: background .2s, color .2s; }
  .tc-blogpost-connect-icons a:hover { background: #0066E6; color: #fff; }

  @media (max-width: 960px) {
    .tc-blogpost-main { grid-template-columns: minmax(0, 1fr); gap: 40px; }
    .tc-blogpost-sidebar { position: static; max-width: 460px; }
    .tc-blogpost-hero-grid { grid-template-columns: minmax(0, 1fr); gap: 32px; }
  }
  @media (max-width: 560px) {
    .tc-blogpost-header { padding-top: 24px; }
    .tc-blogpost-body { font-size: 16px; }
    .tc-blogpost-main { margin-bottom: 72px; }
  }
`;
